import { useState } from "react";
import type { PrinterProfile, Material, PriceEstimate } from "../lib/data";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import {
	ArrowLeft,
	Upload,
	FileBox,
	Scale,
	DollarSign,
	Shield,
	Lock,
	CheckCircle2,
	Printer,
	Package,
	AlertCircle,
	Clock,
} from "lucide-react";

interface OrderPageProps {
	printer: PrinterProfile;
	onBack: () => void;
	onOrderCreated: () => void;
}

export function OrderPage({ printer, onBack, onOrderCreated }: OrderPageProps) {
	const [file, setFile] = useState<File | null>(null);
	const [material, setMaterial] = useState<Material>(printer.materials[0]);
	const [color, setColor] = useState("Black");
	const [quantity, setQuantity] = useState(1);
	const [notes, setNotes] = useState("");
	const [weight, setWeight] = useState(50);
	const [estimate, setEstimate] = useState<PriceEstimate | null>(null);
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState<"upload" | "configure" | "checkout" | "confirmed">("upload");
	const [submitting, setSubmitting] = useState(false);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (f) {
			setFile(f);
			// Simulate weight estimation based on file size
			const estimatedWeight = Math.max(10, Math.min(500, Math.round(f.size / 50000)));
			setWeight(estimatedWeight);
			setStep("configure");
			fetchEstimate(material, estimatedWeight);
		}
	};

	const fetchEstimate = async (mat: Material, w: number) => {
		setLoading(true);
		try {
			const res = await fetch("/api/estimate", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ material: mat, weightGrams: w * quantity, printerId: printer.id }),
			});
			const data = await res.json();
			setEstimate(data);
		} finally {
			setLoading(false);
		}
	};

	const handleMaterialChange = (mat: Material) => {
		setMaterial(mat);
		fetchEstimate(mat, weight);
	};

	const handleQuantityChange = (q: number) => {
		const newQ = Math.max(1, q);
		setQuantity(newQ);
		fetchEstimate(material, weight);
	};

	const handleSubmitOrder = async () => {
		if (!estimate || !file) return;
		setSubmitting(true);
		try {
			await fetch("/api/orders", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					printerId: printer.id,
					printerName: printer.name,
					fileName: file.name,
					fileSize: file.size,
					material,
					color,
					quantity,
					estimatedPrice: estimate.total,
					notes,
					dimensions: { x: 80, y: 60, z: 40 },
					weight: weight * quantity,
					estimatedCompletion: new Date(
						Date.now() + estimate.estimatedHours * 3600000 + 86400000
					).toISOString(),
				}),
			});
			setStep("confirmed");
		} finally {
			setSubmitting(false);
		}
	};

	const colors = ["Black", "White", "Grey", "Red", "Blue", "Green", "Orange", "Yellow"];

	return (
		<div className="max-w-4xl mx-auto space-y-6">
			<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 text-muted-foreground hover:text-foreground">
				<ArrowLeft className="w-4 h-4" />
				Back to {printer.name}
			</Button>

			{/* Printer summary */}
			<Card>
				<CardContent className="p-4">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
							<Printer className="w-5 h-5 text-primary" />
						</div>
						<div>
							<div className="font-medium text-sm">{printer.name}</div>
							<div className="text-xs text-muted-foreground">{printer.printerModel} · {printer.location.city}</div>
						</div>
						<Badge variant="secondary" className="ml-auto">{printer.rating} stars</Badge>
					</div>
				</CardContent>
			</Card>

			{/* Progress steps */}
			<div className="flex items-center gap-2">
				{["Upload File", "Configure", "Checkout"].map((label, i) => {
					const stepIdx = ["upload", "configure", "checkout", "confirmed"].indexOf(step);
					const isActive = i <= stepIdx;
					const isCurrent = i === stepIdx;
					return (
						<div key={label} className="flex items-center gap-2 flex-1">
							<div
								className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
									isActive
										? "bg-primary text-primary-foreground"
										: "bg-muted text-muted-foreground"
								}`}
							>
								{i < stepIdx ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
							</div>
							<span className={`text-sm ${isCurrent ? "font-medium" : "text-muted-foreground"}`}>
								{label}
							</span>
							{i < 2 && <div className={`flex-1 h-px ${isActive ? "bg-primary" : "bg-border"}`} />}
						</div>
					);
				})}
			</div>

			{step === "confirmed" ? (
				<Card>
					<CardContent className="p-12 text-center">
						<div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
							<CheckCircle2 className="w-8 h-8 text-green-600" />
						</div>
						<h2 className="text-2xl font-bold mb-2">Order Placed!</h2>
						<p className="text-muted-foreground mb-6">
							Your print request has been sent to {printer.name}. Payment is held in escrow until you confirm delivery.
						</p>
						<div className="flex gap-3 justify-center">
							<Button onClick={onOrderCreated}>View My Orders</Button>
							<Button variant="outline" onClick={onBack}>
								Back to Printer
							</Button>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Main content */}
					<div className="lg:col-span-2 space-y-4">
						{/* File upload */}
						<Card>
							<CardContent className="p-6">
								<h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
									<Upload className="w-5 h-5 text-muted-foreground" />
									Upload 3D File
								</h2>
								<label
									className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
										file
											? "border-primary/50 bg-primary/5"
											: "border-border hover:border-primary/30 hover:bg-muted/50"
									}`}
								>
									<input
										type="file"
										accept=".stl,.step,.stp,.obj,.3mf"
										onChange={handleFileSelect}
										className="hidden"
									/>
									{file ? (
										<div>
											<FileBox className="w-10 h-10 mx-auto text-primary mb-2" />
											<p className="font-medium">{file.name}</p>
											<p className="text-sm text-muted-foreground mt-1">
												{(file.size / 1024 / 1024).toFixed(2)} MB · Est. {weight}g
											</p>
										</div>
									) : (
										<div>
											<Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
											<p className="font-medium">Drop your 3D file here or click to browse</p>
											<p className="text-sm text-muted-foreground mt-1">
												STL, STEP, OBJ, 3MF supported (max 100MB)
											</p>
										</div>
									)}
								</label>
							</CardContent>
						</Card>

						{/* Configuration */}
						{step !== "upload" && (
							<Card>
								<CardContent className="p-6">
									<h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
										<Scale className="w-5 h-5 text-muted-foreground" />
										Print Configuration
									</h2>
									<div className="space-y-4">
										{/* Material */}
										<div>
											<Label className="text-sm font-medium mb-2 block">Material</Label>
											<div className="flex flex-wrap gap-2">
												{printer.materials.map((mat) => (
													<Button
														key={mat}
														variant={material === mat ? "default" : "outline"}
														size="sm"
														onClick={() => handleMaterialChange(mat)}
													>
														{mat}
														<span className="ml-1 text-xs opacity-70">
															${printer.pricePerGram[mat]?.toFixed(2)}/g
														</span>
													</Button>
												))}
											</div>
										</div>

										{/* Color */}
										<div>
											<Label className="text-sm font-medium mb-2 block">Color</Label>
											<div className="flex flex-wrap gap-2">
												{colors.map((c) => (
													<button
														key={c}
														onClick={() => setColor(c)}
														className={`w-8 h-8 rounded-full border-2 transition-all ${
															color === c ? "border-primary scale-110" : "border-border"
														}`}
														style={{
															backgroundColor:
																c === "White" ? "#f5f5f5" :
																c === "Grey" ? "#9ca3af" :
																c === "Black" ? "#1f2937" :
																c.toLowerCase(),
														}}
														title={c}
													/>
												))}
											</div>
										</div>

										{/* Quantity */}
										<div>
											<Label className="text-sm font-medium mb-2 block">Quantity</Label>
											<div className="flex items-center gap-3">
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleQuantityChange(quantity - 1)}
													disabled={quantity <= 1}
												>
													-
												</Button>
												<span className="text-lg font-semibold w-8 text-center">{quantity}</span>
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleQuantityChange(quantity + 1)}
												>
													+
												</Button>
											</div>
										</div>

										{/* Notes */}
										<div>
											<Label className="text-sm font-medium mb-2 block">Special Instructions</Label>
											<Textarea
												placeholder="Any specific requirements (layer height, infill, orientation, etc.)"
												value={notes}
												onChange={(e) => setNotes(e.target.value)}
												rows={3}
											/>
										</div>

										<Button
											onClick={() => setStep("checkout")}
											disabled={!estimate}
											className="w-full"
										>
											Continue to Checkout
										</Button>
									</div>
								</CardContent>
							</Card>
						)}

						{/* Checkout */}
						{step === "checkout" && (
							<Card>
								<CardContent className="p-6">
									<h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
										<Lock className="w-5 h-5 text-muted-foreground" />
										Secure Checkout
									</h2>
									<div className="space-y-4">
										<div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
											<div className="flex items-start gap-3">
												<Shield className="w-5 h-5 text-blue-500 mt-0.5" />
												<div>
													<p className="text-sm font-medium text-blue-900 dark:text-blue-100">
														Escrow Payment Protection
													</p>
													<p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
														Your payment will be held securely until you confirm the print has been
														received and meets quality standards.
													</p>
												</div>
											</div>
										</div>

										<div>
											<Label className="text-sm font-medium mb-2 block">Card Number</Label>
											<Input placeholder="4242 4242 4242 4242" />
										</div>
										<div className="grid grid-cols-2 gap-3">
											<div>
												<Label className="text-sm font-medium mb-2 block">Expiry</Label>
												<Input placeholder="MM/YY" />
											</div>
											<div>
												<Label className="text-sm font-medium mb-2 block">CVC</Label>
												<Input placeholder="123" />
											</div>
										</div>

										<Button
											onClick={handleSubmitOrder}
											disabled={submitting}
											className="w-full"
											size="lg"
										>
											{submitting ? (
												"Processing..."
											) : (
												<>
													<Lock className="w-4 h-4 mr-2" />
													Pay ${estimate?.total.toFixed(2)} (Held in Escrow)
												</>
											)}
										</Button>
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					{/* Price sidebar */}
					<div className="space-y-4">
						<Card className="sticky top-4">
							<CardContent className="p-6">
								<h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
									<DollarSign className="w-5 h-5 text-muted-foreground" />
									Price Estimate
								</h3>
								{loading ? (
									<div className="text-center text-sm text-muted-foreground py-8">
										Calculating...
									</div>
								) : estimate ? (
									<div className="space-y-3">
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">Base fee</span>
											<span>${estimate.baseCost.toFixed(2)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">Material ({estimate.material})</span>
											<span>${estimate.materialCost.toFixed(2)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">Labor</span>
											<span>${estimate.laborCost.toFixed(2)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">Platform fee (10%)</span>
											<span>${estimate.platformFee.toFixed(2)}</span>
										</div>
										<Separator />
										<div className="flex justify-between font-semibold text-lg">
											<span>Total</span>
											<span>${estimate.total.toFixed(2)}</span>
										</div>
										{quantity > 1 && (
											<div className="text-xs text-muted-foreground text-center">
												${(estimate.total / quantity).toFixed(2)} per unit x {quantity}
											</div>
										)}
										<Separator />
										<div className="flex items-center gap-2 text-xs text-muted-foreground">
											<Clock className="w-3.5 h-3.5" />
											Est. {estimate.estimatedHours} hours print time
										</div>
										<div className="flex items-center gap-2 text-xs text-muted-foreground">
											<Scale className="w-3.5 h-3.5" />
											{estimate.weightGrams}g total weight
										</div>
									</div>
								) : (
									<div className="text-center py-8">
										<AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
										<p className="text-sm text-muted-foreground">
											Upload a file to get a price estimate
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			)}
		</div>
	);
}
